import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './StravaLogin.css';

const WEEK = [
  { day: 'MON', type: 'easy',     label: 'Easy',     detail: '6 mi',  width: '42%' },
  { day: 'TUE', type: 'rest',     label: 'Rest',     detail: '',       width: '6%'  },
  { day: 'WED', type: 'tempo',    label: 'Tempo',    detail: '8 mi',  width: '62%' },
  { day: 'THU', type: 'rest',     label: 'Rest',     detail: '',       width: '6%'  },
  { day: 'FRI', type: 'easy',     label: 'Easy',     detail: '5 mi',  width: '36%' },
  { day: 'SAT', type: 'long',     label: 'Long Run', detail: '16 mi', width: '88%' },
  { day: 'SUN', type: 'recovery', label: 'Recovery', detail: '3 mi',  width: '22%' },
];

const StravaLogin: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="lp-root">

      {/* ── NAV ── */}
      <nav className="lp-nav">
        <span className="lp-logo">My Next Run</span>
        <div className="lp-nav-actions">
          <button className="lp-nav-calc" onClick={() => navigate('/races')}>
            Find a Race
          </button>
          <button className="lp-nav-calc" onClick={() => navigate('/calculator')}>
            Race Calculator
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="lp-hero">
        <div className="lp-hero-left">
          <div className="lp-badge">AI-Powered Marathon Coach</div>
          <h1 className="lp-headline">
            Train.<br />
            Adapt.<br />
            <span className="lp-headline-accent">Race.</span>
          </h1>
          <p className="lp-sub">
            Adaptive plans built on your Strava data — not a generic template.
            Your coach adjusts in real time as life gets in the way.
          </p>
          <div className="lp-cta-stack">
            <button className="lp-strava-btn" onClick={() => login()}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.172" fill="#fff"/>
              </svg>
              Connect with Strava
            </button>
            <button className="lp-calc-btn" onClick={() => navigate('/calculator')}>
              Try Race Calculator →
            </button>
          </div>
        </div>

        {/* ── TRAINING WEEK VISUALIZATION ── */}
        <div className="lp-hero-right">
          <div className="lp-vis-card">
            <div className="lp-vis-header">
              <span className="lp-vis-title">This week's plan</span>
              <span className="lp-vis-meta">Week 8 of 16</span>
            </div>
            <div className="lp-vis-rows">
              {WEEK.map((d, i) => (
                <div className="lp-vis-row" key={d.day} style={{ animationDelay: `${i * 0.07}s` }}>
                  <span className="lp-vis-day">{d.day}</span>
                  <div className="lp-vis-track">
                    <div
                      className={`lp-vis-bar lp-vis-bar--${d.type}`}
                      style={{ '--bar-w': d.width } as React.CSSProperties}
                    />
                  </div>
                  <span className={`lp-vis-label lp-vis-label--${d.type}`}>
                    {d.label}{d.detail ? ` · ${d.detail}` : ''}
                  </span>
                </div>
              ))}
            </div>
            <div className="lp-vis-footer">
              <span>38 mi this week</span>
              <span className="lp-vis-pace">Goal pace 9:08 /mi</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES — asymmetric, not three equal boxes ── */}
      <section className="lp-features">
        <div className="lp-feature-main">
          <span className="lp-feature-num">01</span>
          <h2 className="lp-feature-heading">Plans that adapt, not just plan</h2>
          <p className="lp-feature-body">
            Most training apps hand you a static PDF. My Next Run watches every run you complete,
            every workout you miss, and adjusts the next week accordingly. Miss your long run?
            The plan absorbs it. Crush your tempo? It pushes you harder.
          </p>
        </div>
        <div className="lp-feature-stack">
          <div className="lp-feature-small">
            <span className="lp-feature-num">02</span>
            <h3>Strava, already connected</h3>
            <p>Every run syncs automatically. No logging, no friction.</p>
          </div>
          <div className="lp-feature-small">
            <span className="lp-feature-num">03</span>
            <h3>An AI coach that knows you</h3>
            <p>Ask anything — injury, fatigue, pacing. It answers based on your actual data, not a generic script.</p>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="lp-footer">
        <p>Connect your Strava account to create your free training plan.</p>
      </footer>

    </div>
  );
};

export default StravaLogin;
